import { Event, UnsignedEvent } from 'nostr-tools';
import { finalizeEvent } from 'nostr-tools/pure';
import { RELAYS } from '../config/constants';

interface PublishResult {
    url: string;
    status: 'success' | 'error' | 'timeout';
    data?: string;
    error?: unknown;
}

/**
 * Internal helper to publish a signed event to configured relays
 */
const publishToRelays = async (signedEvent: Event): Promise<PublishResult[]> => {
    const publishPromises = RELAYS.map(async (url: string): Promise<PublishResult> => {
        return new Promise((resolve) => {
            const socket = new WebSocket(url);
            const timeout = setTimeout(() => {
                socket.close();
                resolve({ url, status: 'timeout' });
            }, 3000);

            socket.onopen = () => {
                socket.send(JSON.stringify(['EVENT', signedEvent]));
            };

            socket.onmessage = (msg) => {
                clearTimeout(timeout);
                socket.close();
                resolve({ url, status: 'success', data: msg.data });
            };

            socket.onerror = (error) => {
                clearTimeout(timeout);
                socket.close();
                resolve({ url, status: 'error', error });
            };
        });
    });

    const results = await Promise.allSettled(publishPromises);
    return results
        .filter((result): result is PromiseFulfilledResult<PublishResult> => result.status === 'fulfilled')
        .map(result => result.value);
};

/**
 * Internal helper to sign an event (local or extension)
 */
const signEvent = async (
    eventTemplate: Omit<UnsignedEvent, 'pubkey'>,
    sk?: Uint8Array | null
): Promise<Event> => {
    if (sk) {
        // Add pubkey from secret key for local signing
        const pubkey = import('nostr-tools/pure').then(m => m.getPublicKey(sk));
        const fullEvent: UnsignedEvent = { ...eventTemplate, pubkey: await pubkey };
        return finalizeEvent(fullEvent, sk);
    } else {
        if (!window.nostr) {
            throw new Error('Nostr extension not found');
        }
        const pubkey = await window.nostr.getPublicKey();
        const eventWithPubkey = { ...eventTemplate, pubkey };
        return await window.nostr.signEvent(eventWithPubkey);
    }
}

export const publishEntry = async (
    content: string,
    topic: string,
    sk?: Uint8Array | null,
    createdAt?: number,
    languageCode: string = 'tr' // ISO-639-1 language code
): Promise<PublishResult[]> => {
    // Format content as: topic; \n\n contents
    const formattedContent = `${topic};\n\n${content.trim()}`

    // Build tags - hashtags go here, NOT in content
    const tags: string[][] = [
        ['subject', topic],
        ['t', topic.toLowerCase().trim()],
        ['t', 'turkstr'],
        ['t', 'turkce'],
        ['t', 'turkish'],
        ['t', 'mavililer'],
        ['L', 'ISO-639-1'],
        ['l', languageCode, 'ISO-639-1']
    ]

    const eventTemplate = {
        kind: 1,
        created_at: createdAt || Math.floor(Date.now() / 1000),
        tags,
        content: formattedContent,
    } as Omit<UnsignedEvent, 'pubkey'>;

    const signedEvent = await signEvent(eventTemplate, sk);
    return await publishToRelays(signedEvent);
};

export const deleteEvent = async (
    eventId: string,
    reason: string = 'Legal Requirement',
    sk?: Uint8Array | null,
    topic?: string // Topic for filtering
): Promise<PublishResult[]> => {
    const tags: string[][] = [['e', eventId]]

    // Add topic tag so Kind 5 events can be caught by topic subscription
    if (topic) {
        tags.push(['t', topic.toLowerCase().trim()])
        tags.push(['t', 'mavililer'])
    }

    const eventTemplate = {
        kind: 5,
        created_at: Math.floor(Date.now() / 1000),
        tags,
        content: reason,
    } as Omit<UnsignedEvent, 'pubkey'>;

    const signedEvent = await signEvent(eventTemplate, sk);
    return await publishToRelays(signedEvent);
};

/**
 * React to an event with NIP-25 reaction
 * Content can be:
 * - '+' for like/upvote
 * - '-' for dislike/downvote
 * - Any emoji for emoji reaction
 */
export const reactToEvent = async (
    eventId: string,
    authorPubkey: string,
    content: string, // '+', '-', or emoji like '🔥', '❤️', '😂'
    sk?: Uint8Array | null,
    topic?: string
): Promise<PublishResult[]> => {
    const tags: string[][] = [
        ['e', eventId],
        ['p', authorPubkey]
    ];

    if (topic) {
        tags.push(['t', topic]);
        tags.push(['t', 'mavililer']);
    }

    // For custom emoji reactions, add emoji tag
    if (content.length > 1 && !content.startsWith('+') && !content.startsWith('-')) {
        tags.push(['emoji', content, '']); // NIP-25 emoji tag
    }

    const eventTemplate = {
        kind: 7,
        created_at: Math.floor(Date.now() / 1000),
        tags: tags,
        content: content,
    } as Omit<UnsignedEvent, 'pubkey'>;

    const signedEvent = await signEvent(eventTemplate, sk);
    return await publishToRelays(signedEvent);
};

interface ProfileMetadata {
    name?: string;
    about?: string;
    picture?: string;
    [key: string]: string | undefined;
}

export const updateProfile = async (
    metadata: ProfileMetadata,
    sk?: Uint8Array | null
): Promise<PublishResult[]> => {
    const eventTemplate = {
        kind: 0,
        created_at: Math.floor(Date.now() / 1000),
        tags: [],
        content: JSON.stringify(metadata),
    } as Omit<UnsignedEvent, 'pubkey'>;

    const signedEvent = await signEvent(eventTemplate, sk);
    return await publishToRelays(signedEvent);
};
export const publishRoomMessage = async (
    content: string,
    roomId: string,
    sk?: Uint8Array | null
): Promise<PublishResult[]> => {
    const eventTemplate = {
        kind: 9, // NIP-29 Group Chat Message
        created_at: Math.floor(Date.now() / 1000),
        tags: [
            ['h', roomId],
            ['t', 'mavililer']
        ],
        content: content,
    } as Omit<UnsignedEvent, 'pubkey'>;

    const signedEvent = await signEvent(eventTemplate, sk);
    return await publishToRelays(signedEvent);
};

/**
 * Publish a Kind 3 follow list (contact list)
 * @param followingPubkeys Array of pubkeys to follow
 * @param sk Optional secret key for local signing
 */
export const publishFollowList = async (
    followingPubkeys: string[],
    sk?: Uint8Array | null
): Promise<PublishResult[]> => {
    const eventTemplate = {
        kind: 3, // Contact List
        created_at: Math.floor(Date.now() / 1000),
        tags: followingPubkeys.map(pubkey => ['p', pubkey]),
        content: '', // Can be used for relay recommendations in JSON format
    } as Omit<UnsignedEvent, 'pubkey'>;

    const signedEvent = await signEvent(eventTemplate, sk);
    return await publishToRelays(signedEvent);
};
/**
 * Publish App Configuration (Kind 30005)
 * Used by admins to toggle global features
 */
export const publishAdminConfig = async (
    settings: any,
    sk?: Uint8Array | null
): Promise<PublishResult[]> => {
    const tags: string[][] = [
        ['d', 'mavililer_config'],
        ['system', 'isolateCommunity', settings.isolateCommunity.toString()]
    ];

    // Add feature flags as tags
    Object.entries(settings.features).forEach(([key, value]) => {
        tags.push(['feature', key, value!.toString()]);
    });

    // Add banned words as tags
    settings.bannedWords.forEach((word: string) => {
        tags.push(['word', word]);
    });

    // Add authorized bots as tags
    if (settings.authorizedBots) {
        settings.authorizedBots.forEach((pubkey: string) => {
            tags.push(['bot', pubkey]);
        });
    }

    const eventTemplate = {
        kind: 30005,
        created_at: Math.floor(Date.now() / 1000),
        tags,
        content: 'Mavililer Sözlük Global Config',
    } as Omit<UnsignedEvent, 'pubkey'>;

    const signedEvent = await signEvent(eventTemplate, sk);
    return await publishToRelays(signedEvent);
};
